// ── CAPR S2 — the ASSESSMENT SCREEN (FR-N3 / UX-05), a mode of the Hub's workflow panel ─────────
//
// ⚠ WHAT THE ASSESSOR SEES BEFORE THEY CHOOSE: the prior decision (or that there is none), the
//   control's CURRENT definition, and the evidence linked to it with its flags. Then they choose a
//   status — "Reconfirm <prior>" is one choice — write the rationale (always required), and select
//   the evidence they rely on, each with a note. This is the PROPOSAL; the approver reviews exactly it.
//
// ⚠ THE FLAGS ARE THE EVIDENCE PANEL'S OWN ROW (exported, not forked), so this screen and the
//   Evidence tab cannot describe the same file two ways.
//
// ⚠ CAPR S4 — EACH ITEM SHOWS THE SERVER'S VERDICT NOW (the Hub's evidence_verification, the same map
//   the approver's view reads, rendered by the same EvidenceVerdict), so the assessor sees what their
//   note is acknowledging. Nothing here derives a verdict from the row's flags.
//
// ⚠ NO STATUS IS PRE-SELECTED. A default would let a rationale be written against a status nobody
//   chose. Resuming returned work starts from what was proposed before (preserved on return).

import { useMemo, useState } from 'react';
import {
  CTS_IMPLEMENTATION_STATUSES, CTS_IMPLEMENTATION_STATUS_LABEL, CTS_IMPLEMENTATION_STATUS_HELP,
  type CtsImplementationStatus,
} from '@grc/shared';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { ArtifactRow } from '@/components/compliance/CapturedEvidencePanel';
import type { EvidenceArtifact } from '@/api/types/evidence-artifacts.types';
import type { ControlHub, HubAssignment } from '@/api/types/cts-workspace.types';
import type { CtsProposalPayload } from '@/api/hooks/useCtsTasks';
import { PriorDecisionPanel, ControlDefinition } from './AssessmentContext';
import { EvidenceVerdict } from './EvidenceVerdict';
import {
  ACK_NOTE_HINT, ACK_NOTE_LABEL, NO_EVIDENCE_ACKNOWLEDGE, NO_EVIDENCE_EXPLAINER, NO_EVIDENCE_LABEL,
  PRIOR_BASIS_MARKER, SUBMIT_ACKNOWLEDGE, SUBMIT_ACTION, SUBMIT_WARNING, VERIFICATION_NOW_NOTE, reconfirmLabel,
} from './assessment-copy';

const EYEBROW = 'text-[10px] font-bold uppercase tracking-[0.12em] text-text-secondary';
const FIELD = 'w-full rounded-[7px] border border-border-subtle bg-bg-surface px-[9px] py-[6px] text-[12.5px] text-text-primary';

/** The evidence ids the latest APPROVED decision relied on — the prior-basis markers (UX-05). */
export function priorBasisIds(hub: ControlHub): Set<string> {
  const last = hub.decisions.find((d) => d.outcome_kind === 'approved');
  const items = last?.evidence_basis?.kind === 'items' ? last.evidence_basis.items : [];
  return new Set(items.map((i) => i.evidence_id));
}

/**
 * The status choices, in order. With a prior status, "Reconfirm <prior>" comes FIRST and the prior
 * value is not offered a second time under its plain name — reconfirming is one choice, not two.
 */
export function statusChoices(prior: CtsImplementationStatus | null): { value: CtsImplementationStatus; label: string }[] {
  const rest = CTS_IMPLEMENTATION_STATUSES.filter((s) => s !== prior)
    .map((s) => ({ value: s, label: CTS_IMPLEMENTATION_STATUS_LABEL[s] }));
  return prior ? [{ value: prior, label: reconfirmLabel(prior) }, ...rest] : rest;
}

export function AssessmentForm({ hub, assignment, artifacts, busy, error, onSubmit, onCancel }: {
  hub: ControlHub;
  assignment: HubAssignment;
  artifacts: EvidenceArtifact[];
  busy: boolean;
  error: string | null;
  onSubmit: (proposal: CtsProposalPayload) => void;
  onCancel: () => void;
}) {
  const prior = hub.current_status?.implementation_status ?? null;
  const priorIds = useMemo(() => priorBasisIds(hub), [hub]);
  const [status, setStatus] = useState<CtsImplementationStatus | null>(assignment.proposed_implementation_status);
  const [rationale, setRationale] = useState(assignment.proposed_rationale ?? '');
  const [notes, setNotes] = useState<Map<string, string>>(
    () => new Map((assignment.proposed_evidence ?? []).map((e) => [e.artifactId, e.acknowledgmentNote])));
  const [confirming, setConfirming] = useState(false);
  const [ack, setAck] = useState(false);
  const [noneAck, setNoneAck] = useState(false);

  // ⓘ A previously selected item that is no longer listed (withdrawn / unlinked since) cannot be
  //   submitted again — the server would refuse it — so it is named and left out, never silently kept.
  const listed = new Set(artifacts.map((a) => a.artifact_id));
  const dropped = [...notes.keys()].filter((id) => !listed.has(id));
  const selected = [...notes.entries()].filter(([id]) => listed.has(id));
  const missingNote = selected.some(([, n]) => n.trim() === '');
  const ready = status !== null && rationale.trim() !== '' && !missingNote;

  const toggle = (id: string, on: boolean) => setNotes((m) => {
    const next = new Map(m);
    if (on) next.set(id, next.get(id) ?? ''); else next.delete(id);
    return next;
  });
  const close = () => { setConfirming(false); setAck(false); setNoneAck(false); };
  const submit = () => {
    if (!ready || !status) return;
    onSubmit({
      implementationStatus: status,
      rationale: rationale.trim(),
      evidence: selected.map(([artifactId, n]) => ({ artifactId, acknowledgmentNote: n.trim() })),
    });
  };

  return (
    <div className="flex flex-col gap-[14px]" data-testid="assessment-form">
      <PriorDecisionPanel hub={hub} />
      <ControlDefinition hub={hub} />

      <fieldset className="flex flex-col gap-[6px]" data-testid="status-picker">
        <legend className={EYEBROW}>Implementation status</legend>
        {statusChoices(prior).map((c) => (
          <label key={c.value} className="flex items-start gap-[8px] text-[12.5px] text-text-primary">
            <input type="radio" name="impl-status" value={c.value} checked={status === c.value}
              onChange={() => setStatus(c.value)} className="mt-[3px] accent-accent-cyan" />
            <span>
              {c.label}
              {CTS_IMPLEMENTATION_STATUS_HELP[c.value] && (
                <span className="block text-[11.5px] text-text-faint" data-testid={`status-help-${c.value}`}>
                  {CTS_IMPLEMENTATION_STATUS_HELP[c.value]}
                </span>
              )}
            </span>
          </label>
        ))}
      </fieldset>

      <div className="flex flex-col gap-[5px]">
        <label htmlFor="assessment-rationale" className={EYEBROW}>Rationale</label>
        {status === 'not_applicable' && (
          <p className="text-[11.5px] text-text-faint">Explain why this control does not apply.</p>
        )}
        <textarea id="assessment-rationale" rows={4} value={rationale} onChange={(e) => setRationale(e.target.value)}
          className={FIELD} />
      </div>

      <div className="flex flex-col gap-[6px]">
        <span className={EYEBROW}>Supporting evidence (optional)</span>
        {artifacts.length === 0 ? (
          <p className="text-[12px] italic text-text-faint">No evidence is linked to this control.</p>
        ) : (
          <div className="rounded-[8px] border border-border-subtle">
            <p className="border-b border-border-subtle px-[16px] py-[8px] text-[11.5px] text-text-secondary"
               data-testid="verification-now-note">{VERIFICATION_NOW_NOTE}</p>
            {artifacts.map((a) => {
              const on = notes.has(a.artifact_id);
              const v = hub.evidence_verification.items[a.artifact_id] ?? null;
              return (
                <ArtifactRow key={a.artifact_id} a={a}
                  select={{ checked: on, onChange: (v) => toggle(a.artifact_id, v), disabled: busy }}
                  marker={priorIds.has(a.artifact_id) ? PRIOR_BASIS_MARKER : null}>
                  <EvidenceVerdict status={v?.status ?? null} reasons={v?.reasons ?? null} />
                  {on && (
                    <div className="mt-[8px] flex flex-col gap-[4px]">
                      <label htmlFor={`ack-${a.artifact_id}`} className="text-[11.5px] text-text-secondary">{ACK_NOTE_LABEL}</label>
                      <textarea id={`ack-${a.artifact_id}`} rows={2} value={notes.get(a.artifact_id) ?? ''}
                        onChange={(e) => { const v = e.target.value; setNotes((m) => new Map(m).set(a.artifact_id, v)); }}
                        className={FIELD} />
                      <span className="text-[11px] text-text-faint">{ACK_NOTE_HINT}</span>
                    </div>
                  )}
                </ArtifactRow>
              );
            })}
          </div>
        )}
        {dropped.length > 0 && (
          <p className="text-[11.5px] text-cert-warn-fg" data-testid="dropped-evidence">
            {dropped.length === 1 ? 'One item' : `${dropped.length} items`} selected before is no longer linked
            to this control and has been left out.
          </p>
        )}
      </div>

      {error && <p className="text-[12px] text-cert-warn-fg" role="alert">{error}</p>}

      <div className="flex gap-[8px]">
        <Button size="sm" disabled={!ready || busy} onClick={() => setConfirming(true)} data-testid="assessment-submit-open">
          {SUBMIT_ACTION}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onCancel}>Cancel</Button>
      </div>

      <Dialog open={confirming} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent data-testid="assessment-submit-dialog">
          <DialogHeader>
            <DialogTitle>{SUBMIT_ACTION}?</DialogTitle>
            <DialogDescription>{hub.control.display_code} · {hub.control.title}</DialogDescription>
          </DialogHeader>
          <p className="text-sm">
            Proposed status: <strong>{status ? CTS_IMPLEMENTATION_STATUS_LABEL[status] : '—'}</strong>
            {' · '}{selected.length === 0 ? NO_EVIDENCE_LABEL : `${selected.length} evidence item${selected.length === 1 ? '' : 's'}`}
          </p>
          <p className="text-sm">{SUBMIT_WARNING}</p>
          {selected.length === 0 && (
            <>
              <p className="text-sm text-cert-warn-fg" data-testid="no-evidence-warning">{NO_EVIDENCE_EXPLAINER}</p>
              <div className="flex items-center gap-2">
                <Checkbox id="assessment-none-ack" checked={noneAck} onCheckedChange={setNoneAck} />
                <Label htmlFor="assessment-none-ack">{NO_EVIDENCE_ACKNOWLEDGE}</Label>
              </div>
            </>
          )}
          <div className="flex items-center gap-2">
            <Checkbox id="assessment-submit-ack" checked={ack} onCheckedChange={setAck} />
            <Label htmlFor="assessment-submit-ack">{SUBMIT_ACKNOWLEDGE}</Label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={close}>Cancel</Button>
            <Button data-testid="assessment-submit-confirm"
              disabled={!ack || (selected.length === 0 && !noneAck) || busy}
              onClick={() => { close(); submit(); }}>
              {busy ? 'Submitting…' : SUBMIT_ACTION}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
